import React, { Dispatch, useCallback, useMemo, useState } from 'react';
import { includes, union, difference } from 'lodash';

export interface SingleSelectionHandlers {
  isSelected: (row: any) => boolean;
  toggle: (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    row: any,
  ) => void;
  reset: () => void;
}

export interface UseSingleSelectionResult<ID extends string | number> {
  id?: ID, // selected row ID
  row(): any, // row getter
  handlers: SingleSelectionHandlers,
}

export function useSingleSelection<ID extends string | number>(
  getRowId: (row: any) => ID,
  rows?: any[],
): UseSingleSelectionResult<ID> {
  const [selectedRowId, setSelectedRowId] = useState<ID>();

  const getSelectedRow = useCallback(() => {
    if (rows) {
      return rows.find((r) => selectedRowId === getRowId(r));
    }
    return [];
  }, [getRowId, rows, selectedRowId]);

  const isSelected = useCallback(
    (row: any) => selectedRowId === getRowId(row),
    [getRowId, selectedRowId],
  );

  const toggle = useCallback((
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    row: any,
  ) => {
    const rowId = getRowId(row);
    if (selectedRowId === rowId) {
      setSelectedRowId(undefined);
    } else {
      setSelectedRowId(rowId);
    }
  }, [getRowId, selectedRowId]);

  const reset = () => {
    setSelectedRowId(undefined);
  };

  const handlers = useMemo(() => ({
    isSelected,
    toggle,
    reset,
  }), [isSelected, toggle]);

  return {
    id: selectedRowId,
    row: getSelectedRow,
    handlers,
  };
}

export interface MultipleSelectionHandlers {
  isSelected: (row: any) => boolean;
  toggle: (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    row: any,
  ) => void;
  toggleAll: (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    rows: any[],
  ) => void;
  reset: () => void;
}

export interface UseMultipleSelectionResult<
  ID extends string | number,
  Entity extends Object = Object,
> {
  ids: ID[], // list of IDs
  setSelectedRowIds: Dispatch<React.SetStateAction<ID[]>>, // setter for selected IDs
  rows(): Entity[], // lazy list of rows
  handlers: MultipleSelectionHandlers,
}

export function useMultipleSelection<ID extends string | number, Entity extends Object = Object>(
  getRowId: (row: Entity) => ID,
  rows?: Entity[],
): UseMultipleSelectionResult<ID, Entity> {
  const [selectedRowIds, setSelectedRowIds] = useState<ID[]>([]);

  const getSelectedRows = useCallback(() => {
    if (rows) {
      return rows.filter((r) => includes(selectedRowIds, getRowId(r)));
    }
    return [];
  }, [getRowId, rows, selectedRowIds]);

  const isSelected = useCallback(
    (row: any) => includes(selectedRowIds, getRowId(row)),
    [getRowId, selectedRowIds],
  );

  const toggle = useCallback((
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    row: any,
  ) => {
    const rowId = getRowId(row);
    setSelectedRowIds((selected) => {
      if (includes(selected, rowId)) { // Found - remove
        return selected.slice().filter((id) => id !== rowId);
      }
      // Not found - add
      return selected.slice().concat(rowId);
    });
  }, [getRowId]);

  const toggleAll = useCallback((
    event: React.ChangeEvent<HTMLInputElement> | undefined,
    checked: boolean,
    toggledRows: any[],
  ) => {
    if (checked) {
      setSelectedRowIds((selected) => union(selected, toggledRows.map(getRowId)));
    } else {
      setSelectedRowIds((selected) => difference(selected, toggledRows.map(getRowId)));
    }
  }, [getRowId]);

  const reset = useCallback(() => {
    setSelectedRowIds([]);
  }, [setSelectedRowIds]);

  const handlers = useMemo(() => ({
    isSelected,
    toggle,
    toggleAll,
    reset,
  }), [reset, isSelected, toggle, toggleAll]);

  return {
    ids: selectedRowIds,
    setSelectedRowIds,
    rows: getSelectedRows,
    handlers,
  };
}
